import Tab from "@/components/ui/tab";
import { Button, Heading, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import UsersTable from "../users-table";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  useGetPermissions,
  useGetRole,
  useGetRolePermissions,
  useUpdateRole,
  useUpdateRolePermissions,
} from "@/queries/roles";
import { useNavigate, useParams } from "react-router";
import SearchInput from "@/components/common/search-input";
import PermissionsList from "../../permissions-list";
import { useForm } from "react-hook-form";
import { schema, type NewRoleFormData } from "@/schemas/roles/new-roles";
import { zodResolver } from "@hookform/resolvers/zod";
import TextField, { TextAreaField } from "@/components/common/text-field";
import PermissionType from "../../permission-type";
import type { AxiosError } from "axios";
import EditConfirmationModal from "./confirmation-modal";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import {
  useAssignUserToRole,
  useGetUsers,
  useUnassignUserToRole,
} from "@/queries/users/users.api";
import AssignUserDropdown from "../assign-user-dropdown";
import type { UserType } from "@/types/entities/users";

const RoleEdit = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { hasPermission } = useAuthorization();
  const canAssingRoleToUsers = hasPermission(Permissions.users["assign-role"]);
  const canAssignPermissions = hasPermission(
    Permissions.roles["assign-permissions"],
  );

  const {
    mutateAsync: mutateRolePermissions,
    isPending: isLoadingPermissionUpdate,
  } = useUpdateRolePermissions(params.id!);

  const { mutateAsync: mutateRoleInfo, isPending: isLoadingRoleUpdate } =
    useUpdateRole(params.id!);

  const { data: role } = useGetRole(params.id!);
  const { data: permissions, isLoading: isLoadingPermissions } =
    useGetPermissions();
  const { data: rolePermissions, isLoading: isLoadingRolePermissions } =
    useGetRolePermissions(params.id!);

  const { mutateAsync: assignUsersAsync, isPending: isLoadingAssignUsers } =
    useAssignUserToRole(params.id!);
  const { mutateAsync: unassignUsersAsync, isPending: isLoadingUnassignUsers } =
    useUnassignUserToRole(params.id!);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<NewRoleFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: role?.data?.name || "",
      description: role?.data?.description || "",
    },
  });

  const [name, description] = watch(["name", "description"]);

  const permissionsMap = useMemo(() => {
    if (!permissions) return {};

    return permissions.reduce(
      (acc, permission) => {
        acc[permission.group_name] = [
          ...(acc[permission.group_name] || []),
          permission,
        ];
        return acc;
      },
      {} as Record<
        string,
        {
          id: string;
          name: string;
          display_name: string;
          group_name: string;
        }[]
      >,
    );
  }, [permissions]);

  const [localRolePermissions, setRolePermissions] = useState<
    {
      name: string;
      id: string;
    }[]
  >(
    rolePermissions?.map((perm) => {
      return {
        name: perm.name,
        id: perm.id,
      };
    }) || [],
  );

  useEffect(() => {
    setRolePermissions(
      rolePermissions?.map((perm) => {
        return {
          name: perm.name,
          id: perm.id,
        };
      }) || [],
    );
  }, [rolePermissions]);

  const handleCheckboxChange = (id: string) => {
    setRolePermissions((prev) => {
      const exists = prev.some((permission) => permission.id === id);
      if (exists) {
        return prev.filter((permission) => permission.id !== id);
      } else {
        const permission = permissions?.find((p) => p.id === id);
        if (permission) {
          return [...prev, { name: permission.name, id: permission.id }];
        }
        return prev;
      }
    });
  };

  const hasPermissionsLoaded = isLoadingRolePermissions || isLoadingPermissions;
  const hasPermissionsChaged = useMemo(() => {
    return (
      localRolePermissions.length !== rolePermissions?.length ||
      localRolePermissions.some(
        (perm) => !rolePermissions?.some((p) => p.id === perm.id),
      )
    );
  }, [localRolePermissions, rolePermissions]);

  const [activeTab, setActiveTab] = useState<"permissions" | "users">(() => {
    if (canAssignPermissions) {
      return "permissions";
    }
    return "users";
  });

  const { data: users, isLoading: isLoadingUsers } = useGetUsers({
    page: 1,
    per_page: 100000,
    role: params.id!,
    relationships: ["userRoles", "createdBy"],
  });

  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<
    UserType[]
  >([]);
  const [selectedUsersToUnassign, setSelectedUsersToUnassign] = useState<
    string[]
  >([]);

  const handleUserAssigment = (user: UserType) => {
    const userId = user.keycloak_id || "";
    const wasMarkedForUnassignment = selectedUsersToUnassign.includes(userId);
    if (wasMarkedForUnassignment) {
      setSelectedUsersToUnassign((prev) => prev.filter((id) => id !== userId));
    } else {
      setSelectedUsersToAssign((prev) => {
        if (prev.some((u) => u.keycloak_id === userId)) return prev;
        return [...prev, user];
      });
    }
  };

  const onUnassign = (userId: string) => {
    const wasMarkedForAssignment = selectedUsersToAssign.some(
      (user) => user.keycloak_id === userId,
    );
    if (wasMarkedForAssignment) {
      setSelectedUsersToAssign((prev) =>
        prev.filter((user) => user.keycloak_id !== userId),
      );
    } else {
      setSelectedUsersToUnassign((prev) => {
        if (prev.includes(userId)) return prev;
        return [...prev, userId];
      });
    }
  };

  const [localSearchQuery, setLocalSearchQuery] = useState<string>("");
  const filterPermissions = useMemo(() => {
    if (!permissionsMap) return {};
    const filteredPermissions = Object.entries(permissionsMap).reduce(
      (acc, [groupName, permissions]) => {
        const filtered = permissions.filter(
          (permission) =>
            permission.display_name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()) ||
            permission.name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()) ||
            permission.group_name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()),
        );
        if (filtered.length > 0) {
          acc[groupName] = filtered;
        }
        return acc;
      },
      {} as Record<
        string,
        {
          id: string;
          name: string;
          display_name: string;
          group_name: string;
        }[]
      >,
    );

    return filteredPermissions;
  }, [permissionsMap, localSearchQuery]);

  const roleTypeByPermissions = useMemo(() => {
    let type: "admin" | "editor" | "reader" | "empty" = "empty";
    if (localRolePermissions.length === 0) {
      return type;
    }
    if (
      localRolePermissions.some((permission) =>
        permission.name.includes("read"),
      )
    ) {
      type = "reader";
    }
    if (
      localRolePermissions.some((permission) => {
        return (
          permission.name.includes("update") ||
          permission.name.includes("edit") ||
          permission.name.includes("create")
        );
      })
    ) {
      type = "editor";
    }
    if (
      localRolePermissions.some((permission) => {
        return (
          permission.name.includes("roles:create") ||
          permission.name.includes("roles:update") ||
          permission.name.includes("roles:delete") ||
          permission.name.includes("roles:assign-permissions") ||
          permission.name.includes("users:delete") ||
          permission.name.includes("users:assign-role") ||
          permission.name.includes("users:activate") ||
          permission.name.includes("users:create") ||
          permission.name.includes("users:update") ||
          permission.name.includes("users:reset-password")
        );
      })
    ) {
      type = "admin";
    }
    if (type === "empty") {
      type = "editor"; // Default to editor if no permissions match
    }
    return type;
  }, [localRolePermissions]);

  const [tempData, setTempData] = useState<NewRoleFormData | null>(null);
  const onPreSubmit = (data: NewRoleFormData) => {
    setModalState("edit");
    setTempData(data);
  };

  const asignUsersToRole = useCallback(async () => {
    if (!canAssingRoleToUsers || selectedUsersToAssign.length === 0) return;
    try {
      await assignUsersAsync(
        selectedUsersToAssign
          .map((user) => user.keycloak_id || "")
          .filter(Boolean),
      );
    } catch (e) {
      throw e as Error;
    }
  }, [assignUsersAsync, canAssingRoleToUsers, selectedUsersToAssign]);

  const unassignUsersFromRole = useCallback(async () => {
    if (!canAssingRoleToUsers || selectedUsersToUnassign.length === 0) return;
    try {
      await unassignUsersAsync(
        selectedUsersToUnassign.filter(Boolean).filter((userId) => {
          return selectedUsersToAssign.every(
            (user) => user.keycloak_id !== userId,
          );
        }),
      );
    } catch (e) {
      throw e as Error;
    }
  }, [
    unassignUsersAsync,
    canAssingRoleToUsers,
    selectedUsersToUnassign,
    selectedUsersToAssign,
  ]);

  const onSubmit = async (data: NewRoleFormData) => {
    try {
      if (role?.data?.is_editable) {
        await mutateRoleInfo({
          ...data,
          id: params.id!,
          type: roleTypeByPermissions,
        });
      }
      if (hasPermissionsChaged && canAssignPermissions) {
        await mutateRolePermissions(
          localRolePermissions.map((perm) => perm.id),
        );
      }

      await asignUsersToRole();
      await unassignUsersFromRole();
      setSelectedUsersToAssign([]);
      setSelectedUsersToUnassign([]);
      showToast({
        title: "Rol actualizado",
        description: "El rol ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      navigate(`/roles`);
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      if (
        e.status === 409 &&
        e.response?.data.message.includes("already exists")
      ) {
        setError("name", {
          type: "manual",
          message: "El nombre del rol ya existe. Por favor, elige otro.",
        });
      }
      showToast({
        title: "Error al actualizar el rol",
        description: "Ha ocurrido un error al actualizar el rol.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const [modalState, setModalState] = useState<"edit" | "idle">("idle");

  const { setComp } = useHeaderButton();

  useEffect(() => {
    if (role?.data?.is_editable) {
      setComp(
        <>
          <Button
            variant="quiet"
            size="xs"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="xs"
            disabled={
              isLoadingPermissionUpdate ||
              hasPermissionsLoaded ||
              isLoadingRoleUpdate ||
              localRolePermissions.length === 0 ||
              (!hasPermissionsChaged &&
                (!isDirty || !isValid) &&
                canAssingRoleToUsers &&
                selectedUsersToAssign.length === 0 &&
                selectedUsersToUnassign.length === 0) ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers
            }
            form="edit-role-form"
            type="submit"
          >
            Guardar cambios
            {(isLoadingPermissionUpdate ||
              isLoadingRoleUpdate ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers) && (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-current"
              />
            )}
          </Button>
        </>,
      );
    } else {
      setComp(
        <>
          <Button
            variant="quiet"
            size="xs"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="xs"
            type="button"
            disabled={
              (canAssingRoleToUsers &&
                selectedUsersToAssign.length === 0 &&
                selectedUsersToUnassign.length === 0) ||
              isLoadingUnassignUsers ||
              isLoadingAssignUsers
            }
            onClick={async () => {
              try {
                if (canAssingRoleToUsers && selectedUsersToAssign.length > 0) {
                  await asignUsersToRole();
                }
                if (
                  canAssingRoleToUsers &&
                  selectedUsersToUnassign.length > 0
                ) {
                  await unassignUsersFromRole();
                }
                showToast({
                  title: "Rol actualizado",
                  description:
                    "Los usuarios han sido actualizados correctamente.",
                  options: {
                    type: "success",
                    duration: 5000,
                  },
                });
                navigate(`/roles`);
              } catch (e) {
                console.log(e);
                showToast({
                  title: "Error al actualizar el rol",
                  description:
                    "Ha ocurrido un error al actualizar los usuarios del rol.",
                  options: {
                    type: "error",
                    duration: 5000,
                  },
                });
              }
            }}
          >
            Guardar cambios
            {(isLoadingPermissionUpdate ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers) && (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-current"
              />
            )}
          </Button>
        </>,
      );
    }

    return () => {
      setComp(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    role,
    isDirty,
    navigate,
    setComp,
    isValid,
    unassignUsersFromRole,
    asignUsersToRole,
    selectedUsersToUnassign,
    isLoadingUnassignUsers,
    isLoadingAssignUsers,
    canAssingRoleToUsers,
    selectedUsersToAssign,
    isLoadingRoleUpdate,
    hasPermissionsChaged,
    isLoadingPermissionUpdate,
    hasPermissionsLoaded,
    localRolePermissions,
  ]);

  return (
    <div className="h-full grid grid-cols-1 desktop:grid-cols-2 gap-8">
      <EditConfirmationModal
        isOpen={modalState === "edit"}
        onConfirm={() => {
          setModalState("idle");
          if (tempData) {
            onSubmit(tempData);
          }
        }}
        onCancel={() => {
          setModalState("idle");
        }}
      />
      <form
        id="edit-role-form"
        data-testid="edit-role-form"
        onSubmit={handleSubmit(onPreSubmit)}
        className="h-full w-full flex flex-col"
      >
        <Heading variant="h5">Datos del rol</Heading>
        <div className="flex flex-col gap-6 pt-4">
          <TextField
            id="name"
            label="Nombre"
            placeholder="Ingrese el nombre del rol"
            className="w-full"
            variant="lg"
            required
            {...register("name")}
            maxLength={125}
            limit={125}
            count={name.length}
            error={errors.name?.message || ""}
            disabled={role?.data?.is_editable === false}
          />
          <TextAreaField
            id="description"
            label="Descripción"
            placeholder="Ingrese una descripción del rol"
            className="w-full"
            rows={4}
            required
            {...register("description")}
            maxLength={255}
            limit={255}
            count={description.length}
            error={errors.description?.message || ""}
            disabled={role?.data?.is_editable === false}
          />
        </div>
        <div className="flex flex-col gap-2 pt-8">
          <Heading variant="h5">Tipo de rol</Heading>
          <p className="leading-3xl text-body font-normal">
            El tipo de rol está determinado por los permisos activos para los
            usuarios. Aquí podrás ver qué tipo de rol tendrán los usuarios que
            añadas según los permisos que vayas seleccionando.
          </p>
          <div className="pt-6">
            <PermissionType
              name="Administrador"
              description="Permisos de edición sobre Roles y Usuarios"
              isActive={roleTypeByPermissions === "admin"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Editor"
              description="Permisos de edición sobre Procesos, Flujos y demás."
              isActive={roleTypeByPermissions === "editor"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Lector"
              description="Permisos de lectura en todas las partes del sistema."
              isActive={roleTypeByPermissions === "reader"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
              showDivider={false}
            />
          </div>
        </div>
      </form>

      {(canAssignPermissions || canAssingRoleToUsers) && (
        <div className="flex flex-col w-full h-fit shadow-custom bg-neutral-object border-neutral border-[0.5px] rounded-xl overflow-hidden [@media(min-height:800px)]:max-h-[calc(100vh-200px)] max-h-[678px]">
          <div className="flex justify-between items-center px-3">
            <div className="relative pt-3">
              {canAssignPermissions && (
                <Tab
                  active={activeTab === "permissions"}
                  onClick={() => setActiveTab("permissions")}
                >
                  Permisos
                </Tab>
              )}
              <Tab
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              >
                Usuarios
              </Tab>
            </div>
            {activeTab === "permissions" && canAssignPermissions && (
              <SearchInput
                variant="xs"
                placeholder="Buscar permisos..."
                onChange={({ target }) => {
                  setLocalSearchQuery(target.value);
                }}
              />
            )}
            {activeTab === "users" && canAssingRoleToUsers && (
              <div className="pt-3 pb-2">
                <AssignUserDropdown
                  onAddUser={(user) => {
                    handleUserAssigment(user as UserType);
                  }}
                  selectedUsers={selectedUsersToAssign}
                  roleId={params.id}
                />
              </div>
            )}
          </div>
          {activeTab === "permissions" && canAssignPermissions && (
            <PermissionsList
              isLoading={isLoadingPermissions || isLoadingRolePermissions}
              items={filterPermissions}
              handleCheckboxChange={handleCheckboxChange}
              selectedItems={localRolePermissions.map((perm) => perm.id)}
              canEdit={role?.data?.is_editable !== false}
            />
          )}
          {activeTab === "users" && (
            <UsersTable
              data={[
                ...(selectedUsersToAssign || []),
                ...(users?.data || []).filter((user) => {
                  return !selectedUsersToUnassign.includes(
                    user.keycloak_id || "",
                  );
                }),
              ]}
              roleId={params.id!}
              isLoading={!users?.data && isLoadingUsers}
              onUnassign={onUnassign}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RoleEdit;
